/**
 * The firmware registry as sv_gateway serializes it (`GET /admin/firmware/releases`).
 *
 * Two independent update tracks live in it, and they must never be confused:
 *
 *  - `app` — the orchestrator and the Flutter bundle, a ~36 MB archive, installed by
 *    the `ota_update` command in seconds, rolled back by publishing an older build;
 *  - `firmware` — the whole `update.img` (bootloader, kernel, rootfs), ~430 MB,
 *    installed by `firmware_update` with two reboots, and with NO rollback: the board
 *    has a single rootfs partition.
 *
 * Each track has its own "latest", and a terminal reports one version per track
 * (`app_version`, `firmware_version`). They never match each other.
 */
export type FirmwareKind = 'app' | 'firmware';

export type FirmwareRelease = {
  version: string;
  channel: string;
  kind: FirmwareKind;
  sha256: string;
  size_bytes: number;
  notes: string;
  published_at: string;
  installed_count?: number;
};

/** The command that installs each track on a terminal. */
export const INSTALL_COMMAND: Record<FirmwareKind, string> = {
  app: 'ota_update',
  firmware: 'firmware_update',
};

/**
 * Where a terminal stands against the registry.
 *
 * "Latest" is the most recently PUBLISHED build, not the highest number: versions on
 * this fleet come from three build systems and have no order. So the comparison is
 * equality, exactly as the board itself does it (`current.trim() != latest.trim()`),
 * and republishing an older build is a legitimate rollback that shows up here as
 * "update available".
 *
 * An empty reported version is `unknown`, never `outdated`: boards older than
 * 2026-08-20 do not report their image at all, and a terminal synced through a desktop
 * reports in someone else's words.
 */
export type UpdateState = 'current' | 'available' | 'unknown' | 'unpublished';

export function updateState(
  installed: string | null | undefined,
  latest: FirmwareRelease | null | undefined,
): UpdateState {
  if (latest === null) {
    return 'unpublished';
  }
  const have = installed?.trim();
  if (!have || have === 'unknown' || !latest) {
    return 'unknown';
  }
  return have === latest.version.trim() ? 'current' : 'available';
}

export function formatSize(bytes: number): string {
  if (bytes >= 1_073_741_824) {
    return `${(bytes / 1_073_741_824).toFixed(2)} GB`;
  }
  if (bytes >= 1_048_576) {
    return `${(bytes / 1_048_576).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${bytes} B`;
}
